import React, { useMemo, useRef, useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Loader2,
  Plus,
  Trash2,
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  GripVertical,
} from 'lucide-react';
import { Button } from '../ui/button';
import { ASSEMBLE_ARTICLE, PUBLISH_ASSEMBLED_ARTICLE } from '../../graphql';
import { GET_ASSEMBLER_SOURCES } from '../../graphql';
import { Loader } from '../common/PageLoader';
import { Link } from 'react-router-dom';

// The three assembler formats and user needs are fixed enums that mirror the
// apiserver's ASSEMBLER_FORMATS / ASSEMBLER_USER_NEEDS.
const FORMATS = ['Explainer', 'Roundup / Listicle', 'Timeline'];
const USER_NEEDS = [
  'Update me now',
  'Inform me',
  'Break it down for me',
  'Take me deeper',
  'Broaden my perspective',
];
// What the format chips say (Prototype v6); the values above are what the server takes.
// Reader needs show their own names, the same words as the account's User Needs
// taxonomy that Prism AI classifies with (owner, 2026-10-19).
const FORMAT_LABELS: Record<string, string> = { 'Roundup / Listicle': 'Roundup' };

interface Block {
  heading?: string;
  text?: string;
  fact_ids?: string[];
}
interface Flag {
  type: string;
  block_number: number | null;
  problematic_text: string;
  cited_fact_id: string;
  explanation: string;
  severity: 'high' | 'medium' | 'low';
}
interface SourceArticle {
  id: string;
  title: string;
  date: string;
}
interface Assembled {
  suggestedTitle?: string;
  suggestedExcerpt?: string;
  sourceWarning?: string;
  blocks: Block[];
  sourceArticles: SourceArticle[];
  selectionInfo?: { candidates: number; selected: number; reasoning: string };
  factIndex: Record<string, { fact: string; readable_date?: string; attribution?: string }>;
  dedupStats?: { before: number; after: number; groups_merged: number; removed?: number };
  verification: { passed: boolean; flags: Flag[] };
  expectationSummary?: string;
}

type SourceMode = 'ai' | 'filter' | 'manual';

/**
 * What each check is called and asks for, in plain words (the server's own
 * explanation is for the agent). [F3] is the fact a sentence came from.
 */
const FLAG_WORDS: Record<string, [string, string]> = {
  uncited_claim: [
    'Not linked to a fact',
    'This sentence has a number, a date or a quote but no fact marker like [F3]. Add the marker of the fact it comes from, or rephrase it.',
  ],
  invalid_citation: [
    'Points to a fact that isn’t there',
    'The marker doesn’t match any fact used for this draft. Fix the number or remove it.',
  ],
  fabricated_quote: [
    'Quote not in the source',
    'These quoted words aren’t in the fact it cites. Use the source’s exact words.',
  ],
  duplicate_citation: [
    'Same fact used twice',
    'This fact is already used in another block. Keep it in one place.',
  ],
  numeric_dilution: [
    'Number made vaguer',
    'The source gives an exact number; this says “several” or similar. Use the number.',
  ],
  citation_stacking: [
    'Too many facts in one sentence',
    'Split it so each sentence closely follows one or two facts.',
  ],
  relative_time: [
    'Relative date',
    'Words like “Tuesday” or “tomorrow” go stale. Use the date itself.',
  ],
  outdated_future: ['Out of date', 'It says something is still to come on a date that has passed.'],
  over_word_limit: ['Too long', 'The draft is longer than this format allows.'],
  over_block_limit: ['Too many blocks', 'The draft has more blocks than this format allows.'],
  wrong_listicle_count: [
    'Wrong number of items',
    'The roundup has a different number of items than asked for.',
  ],
  wrong_timeline_count: [
    'Wrong number of entries',
    'The timeline has a different number of entries than asked for.',
  ],
  invalid_timeline_heading: [
    'Entry needs a date',
    'Start the heading with its date, like “April 7, 2026 — Ceasefire announced”.',
  ],
  missing_timeline_label: [
    'Entry needs a label',
    'Add a short label after the date, like “— Ceasefire announced”.',
  ],
  timeline_order: ['Out of order', 'The entries aren’t in the order you chose.'],
  duplicate_summary_lead: [
    'Summary repeats the opening',
    'Say something different in the summary.',
  ],
  duplicate_summary_body: ['Summary repeats the text', 'Say something different in the summary.'],
  duplicate_lead_body: ['Opening is repeated', 'The first block repeats a later one.'],
  thin_bottom_line: ['Ending is thin', 'The last block needs a clearer takeaway.'],
};
const flagWords = (f: Flag): [string, string] =>
  FLAG_WORDS[f.type] || [f.type.replace(/_/g, ' '), f.explanation];

const SEVERITY_STYLES: Record<string, string> = {
  high: 'border-red-500/40 bg-red-500/10 text-red-300',
  medium: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  low: 'border-cmp-line-2/40 bg-cmp-surface/10 text-cmp-ink',
};

const inputClass =
  'w-full rounded border border-cmp-line-2 bg-core-dark text-cmp-ink px-3 py-2 text-sm focus:outline-none focus:border-primary';

export function AssemblerWizard({ accountId }: { accountId: string }) {
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 — brief & sources
  const [userNeed, setUserNeed] = useState('');
  const [format, setFormat] = useState(FORMATS[0]);
  const [brief, setBrief] = useState('');
  const [sourceMode, setSourceMode] = useState<SourceMode>('ai');
  const [listicleCount, setListicleCount] = useState(5);
  const [timelineCount, setTimelineCount] = useState(7);
  const [timelineOrder, setTimelineOrder] = useState<'chronological' | 'reverse'>('chronological');
  const [keyword, setKeyword] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Result
  const [result, setResult] = useState<Assembled | null>(null);
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  // Edited since the checks ran: they describe the text as it was assembled.
  const [editedSinceCheck, setEditedSinceCheck] = useState(false);
  const [published, setPublished] = useState<{ id: string; title: string } | null>(null);
  const [error, setError] = useState('');

  const isListicle = /listicle|roundup/i.test(format);
  const isTimeline = /timeline/i.test(format);

  const [loadSources, { data: sourcesData, loading: sourcesLoading }] =
    useLazyQuery(GET_ASSEMBLER_SOURCES);
  const [assemble, { loading: assembling }] = useMutation(ASSEMBLE_ARTICLE);
  const [publish, { loading: publishing }] = useMutation(PUBLISH_ASSEMBLED_ARTICLE);

  const sources: SourceArticle[] = sourcesData?.assemblerSources || [];

  const runLoadSources = () => {
    loadSources({
      variables: {
        accountId,
        mode: sourceMode === 'filter' ? 'filter' : 'ai',
        keyword: sourceMode === 'filter' ? keyword || null : null,
        dateFrom: sourceMode === 'filter' ? dateFrom || null : null,
        dateTo: sourceMode === 'filter' ? dateTo || null : null,
      },
    });
  };

  const runAssemble = async () => {
    setError('');
    if (!brief.trim()) {
      setError('Say what it should cover first.');
      return;
    }
    try {
      const { data } = await assemble({
        variables: {
          accountId,
          input: {
            brief,
            userNeed,
            format,
            sourceMode,
            topicIds: sourceMode === 'manual' ? selectedIds : [],
            keyword: sourceMode === 'filter' ? keyword : null,
            dateFrom: sourceMode === 'filter' ? dateFrom || null : null,
            dateTo: sourceMode === 'filter' ? dateTo || null : null,
            listicleCount: isListicle ? listicleCount : 0,
            timelineCount: isTimeline ? timelineCount : 0,
            timelineOrder: isTimeline ? timelineOrder : 'chronological',
          },
        },
      });
      const r: Assembled = data.assembleArticle;
      setResult(r);
      setEditedSinceCheck(false);
      setTitle(r.suggestedTitle || '');
      setExcerpt(r.suggestedExcerpt || '');
      setStep(2);
    } catch (e: any) {
      setError(e.message || 'Assembly failed.');
    }
  };

  const updateBlock = (i: number, field: 'heading' | 'text', value: string) => {
    setEditedSinceCheck(true);
    setResult((prev) =>
      prev
        ? {
            ...prev,
            blocks: prev.blocks.map((b, idx) => (idx === i ? { ...b, [field]: value } : b)),
          }
        : prev
    );
  };
  const addBlock = () => {
    setEditedSinceCheck(true);
    setResult((prev) =>
      prev ? { ...prev, blocks: [...prev.blocks, { heading: '', text: '' }] } : prev
    );
  };
  const removeBlock = (i: number) => {
    setEditedSinceCheck(true);
    setResult((prev) =>
      prev ? { ...prev, blocks: prev.blocks.filter((_, idx) => idx !== i) } : prev
    );
  };
  // Reorder blocks by drag-and-drop (functional update keeps it stable mid-drag).
  const moveBlock = (from: number, to: number) => {
    setResult((prev) => {
      if (!prev) return prev;
      const blocks = [...prev.blocks];
      const [moved] = blocks.splice(from, 1);
      blocks.splice(to, 0, moved);
      return { ...prev, blocks };
    });
  };

  const renderPreviewBody = (blocks: Block[]) =>
    blocks.map((b, i) => {
      const text = (b.text || '').replace(/\s*\[F\d+\]/g, '');
      return (
        <div key={i} className="mb-4">
          {b.heading ? (
            <h2 className="text-cmp-ink text-lg font-semibold mb-1">{b.heading}</h2>
          ) : null}
          {text.split(/\n{2,}/).map((p, pi) => (
            <p key={pi} className="text-cmp-ink mb-2 leading-relaxed">
              {p}
            </p>
          ))}
        </div>
      );
    });

  const wordCount = useMemo(() => {
    if (!result) return 0;
    return result.blocks.reduce(
      (n, b) => n + ((b.text || '').match(/[A-Za-z0-9']+/g)?.length || 0),
      0
    );
  }, [result]);

  const runPublish = async () => {
    if (!result) return;
    setError('');
    if (!title.trim()) {
      setError('Headline is required.');
      return;
    }
    try {
      const { data } = await publish({
        variables: {
          accountId,
          input: {
            title,
            excerpt,
            blocks: result.blocks,
            // Always a draft: it waits in Content for checks and approval.
            status: 'draft',
            sourceIds: result.sourceArticles.map((s) => s.id),
            brief,
            assemblyFormat: format,
          },
        },
      });
      setPublished(data.publishAssembledArticle);
    } catch (e: any) {
      setError(e.message || 'Publish failed.');
    }
  };

  const resetAll = () => {
    setStep(1);
    setResult(null);
    setPublished(null);
    setTitle('');
    setExcerpt('');
    setError('');
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      {/* Step indicator (from step 2; step 1 is the brief) */}
      <div className={`flex items-center gap-3 mb-6 text-sm ${step === 1 ? 'hidden' : ''}`}>
        {[
          [1, 'Brief and sources'],
          [2, 'Assemble and review'],
          [3, 'Preview and save'],
        ].map(([n, label], idx) => (
          <React.Fragment key={n as number}>
            <div
              className={`flex items-center gap-2 ${step === n ? 'text-cmp-ink' : 'text-cmp-ink-3'}`}
            >
              <span
                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-xs"
                // The app's black primary (ink-inv), not the old blue.
                style={
                  step >= (n as number)
                    ? { background: 'var(--ink-inv)', color: 'var(--ink-inv-ink)' }
                    : { background: 'var(--ink-bg3)', color: 'var(--ink-ink2)' }
                }
              >
                {n}
              </span>
              <span>{label}</span>
            </div>
            {idx < 2 && <div className="h-px w-8 bg-cmp-surface" />}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* ── Step 1 (Prototype v6: brief, format, reader need, sources) ── */}
      {step === 1 && (
        <div className="as-brief">
          <label className="as-label" htmlFor="as-brief">
            What should it cover?
          </label>
          <textarea
            id="as-brief"
            rows={4}
            className="as-textarea"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="e.g. What we know so far about the story, and what happens next."
          />

          <div className="as-label">Format</div>
          <div className="as-chips">
            {FORMATS.map((f) => (
              <button
                key={f}
                type="button"
                className="as-chip"
                data-active={format === f ? 'true' : undefined}
                onClick={() => setFormat(f)}
              >
                {FORMAT_LABELS[f] || f}
              </button>
            ))}
          </div>
          {isListicle ? (
            <label className="as-inline">
              Items
              <input
                type="number"
                min={3}
                max={50}
                className="as-number"
                value={listicleCount}
                onChange={(e) => setListicleCount(parseInt(e.target.value, 10) || 5)}
              />
            </label>
          ) : null}
          {isTimeline ? (
            <div className="as-inline">
              <label>
                Events
                <input
                  type="number"
                  min={4}
                  max={15}
                  className="as-number"
                  value={timelineCount}
                  onChange={(e) => setTimelineCount(parseInt(e.target.value, 10) || 7)}
                />
              </label>
              <span className="ink-seg as-seg">
                {(
                  [
                    ['chronological', 'Oldest first'],
                    ['reverse', 'Newest first'],
                  ] as const
                ).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    data-active={timelineOrder === v ? 'true' : 'false'}
                    onClick={() => setTimelineOrder(v)}
                  >
                    {label}
                  </button>
                ))}
              </span>
            </div>
          ) : null}

          <div className="as-label">
            Reader need <span className="as-optional">· optional</span>
          </div>
          <div className="as-chips">
            {USER_NEEDS.map((u) => (
              <button
                key={u}
                type="button"
                className="as-chip"
                data-active={userNeed === u ? 'true' : undefined}
                onClick={() => setUserNeed(userNeed === u ? '' : u)}
              >
                {u}
              </button>
            ))}
          </div>

          <div className="as-label">Sources</div>
          <span className="ink-seg as-seg">
            {(
              [
                ['ai', 'Agent picks'],
                ['filter', 'Topics & dates'],
                ['manual', 'Specific articles'],
              ] as [SourceMode, string][]
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                data-active={sourceMode === mode ? 'true' : 'false'}
                onClick={() => setSourceMode(mode)}
              >
                {label}
              </button>
            ))}
          </span>
          {sourceMode === 'ai' ? (
            <p className="as-help">
              Your agent picks the 15–20 most relevant articles with facts from your brief.
            </p>
          ) : null}
          {sourceMode === 'filter' ? (
            <div className="as-filter">
              <input
                className="as-input"
                placeholder="Keyword or topic"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              <input
                type="date"
                className="as-input"
                aria-label="From"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <input
                type="date"
                className="as-input"
                aria-label="To"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          ) : null}
          {sourceMode === 'manual' ? (
            <div className="as-manual">
              <button
                type="button"
                className="ink-btn ink-btn-quiet"
                onClick={runLoadSources}
                disabled={sourcesLoading}
              >
                {sourcesLoading ? (
                  <Loader size="inline" />
                ) : sources.length ? (
                  'Reload articles'
                ) : (
                  'Show articles'
                )}
              </button>
              {sources.length ? (
                <div className="as-source-list">
                  {sources.map((s) => (
                    <label key={s.id} className="as-source">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(s.id)}
                        onChange={(e) =>
                          setSelectedIds((prev) =>
                            e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id)
                          )
                        }
                      />
                      <span className="as-source-title">{s.title}</span>
                      <span className="as-source-date">
                        {s.date ? new Date(s.date).toLocaleDateString() : ''}
                      </span>
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="as-go">
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={runAssemble}
              disabled={assembling || !brief.trim()}
            >
              {assembling ? 'Assembling…' : 'Assemble draft'}
            </button>
            <span className="as-help" style={{ margin: 0 }}>
              {assembling
                ? 'Picking sources, gathering their facts and checking every sentence…'
                : 'Every sentence links back to the fact it came from. You review it before anything is published.'}
            </span>
          </div>
        </div>
      )}

      {/* ── Step 2 ── */}
      {step === 2 && result && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-cmp-ink text-sm">
              {result.blocks.length} blocks · {wordCount} words
              {result.dedupStats
                ? ` · ${result.dedupStats.after} of ${result.dedupStats.before} facts used${
                    result.dedupStats.before > result.dedupStats.after
                      ? ` (${result.dedupStats.before - result.dedupStats.after} duplicates left out)`
                      : ''
                  }`
                : ''}
            </div>
            <Button variant="outline" onClick={addBlock}>
              <Plus className="w-4 h-4 mr-1" /> Add block
            </Button>
          </div>

          <VerificationPanel verification={result.verification} edited={editedSinceCheck} />

          <p className="text-cmp-ink-3 text-xs">
            Drag the handle to reorder blocks. A marker like [F3] shows which fact a sentence came
            from; markers are left out of the article.
          </p>
          <DndProvider backend={HTML5Backend}>
            <div className="space-y-3">
              {result.blocks.map((b, i) => (
                <SortableBlock
                  key={i}
                  index={i}
                  block={b}
                  moveBlock={moveBlock}
                  updateBlock={updateBlock}
                  removeBlock={removeBlock}
                />
              ))}
            </div>
          </DndProvider>

          <div className="flex items-center justify-between pt-2">
            <Button variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to brief
            </Button>
            <Button onClick={() => setStep(3)} className="bg-button-primary text-button-primary">
              Preview article <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* ── Step 3 ── */}
      {step === 3 && result && (
        <div className="space-y-4 max-w-3xl">
          {published ? (
            <div className="ink-card" style={{ padding: 18 }}>
              <div
                className="flex items-center gap-2 mb-1"
                style={{ color: 'var(--ink-green-ink)', fontWeight: 600 }}
              >
                <CheckCircle2 className="w-5 h-5" /> Draft saved
              </div>
              <p className="text-sm" style={{ color: 'var(--ink-ink)' }}>
                “{published.title}” is in Content, waiting for your review. Nothing goes live until
                you approve it.
              </p>
              <div className="flex gap-2 mt-3">
                <Link to={`/content/${published.id}`} className="ink-btn ink-btn-primary">
                  Open article
                </Link>
                <button type="button" className="ink-btn" onClick={resetAll}>
                  Assemble another
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-cmp-ink text-sm font-medium mb-1">Headline</label>
                <input
                  className={inputClass}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-cmp-ink text-sm font-medium mb-1">Summary</label>
                <textarea
                  rows={2}
                  className={inputClass}
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                />
              </div>

              <div className="rounded border border-cmp-line-2 bg-core-dark p-4">
                <h1 className="text-cmp-ink text-xl font-bold mb-1">{title}</h1>
                {excerpt && <p className="text-cmp-ink-2 italic mb-4">{excerpt}</p>}
                {renderPreviewBody(result.blocks)}
              </div>

              <div className="text-cmp-ink-2 text-xs">
                {wordCount} words · drawn from {result.sourceArticles.length} source articles
              </div>

              <div className="flex items-center justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to review
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={runPublish}
                    disabled={publishing}
                    className="bg-button-primary text-button-primary"
                  >
                    {publishing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                      </>
                    ) : (
                      'Save draft for review'
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const BLOCK_DND_TYPE = 'ASSEMBLER_BLOCK';

function SortableBlock({
  index,
  block,
  moveBlock,
  updateBlock,
  removeBlock,
}: {
  index: number;
  block: Block;
  moveBlock: (from: number, to: number) => void;
  updateBlock: (i: number, field: 'heading' | 'text', value: string) => void;
  removeBlock: (i: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);

  const [, drop] = useDrop<{ index: number }>({
    accept: BLOCK_DND_TYPE,
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const rect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (rect.bottom - rect.top) / 2;
      const offset = monitor.getClientOffset();
      if (!offset) return;
      const hoverClientY = offset.y - rect.top;
      // Only cross the midpoint to avoid flicker when dragging over a neighbor.
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      moveBlock(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: BLOCK_DND_TYPE,
    item: () => ({ index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  // The whole card is the drop target + drag preview; only the grip drags.
  preview(drop(ref));
  drag(handleRef);

  return (
    <div
      ref={ref}
      className={`rounded border border-cmp-line-2 bg-core-dark p-3 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <button
            ref={handleRef}
            className="cursor-move text-cmp-ink-3 hover:text-cmp-ink"
            aria-label="Drag to reorder block"
            title="Drag to reorder"
            type="button"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <span className="text-cmp-ink-3 text-xs">Block {index + 1}</span>
        </div>
        <button
          onClick={() => removeBlock(index)}
          className="text-cmp-ink-3 hover:text-red-400"
          aria-label="Remove block"
          type="button"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <input
        className={`${inputClass} mb-2`}
        placeholder="Heading (optional)"
        value={block.heading || ''}
        onChange={(e) => updateBlock(index, 'heading', e.target.value)}
      />
      <textarea
        rows={5}
        className={inputClass}
        value={block.text || ''}
        onChange={(e) => updateBlock(index, 'text', e.target.value)}
      />
      {block.fact_ids && block.fact_ids.length > 0 && (
        <div className="mt-1 text-xs text-cmp-ink-3">Cites: {block.fact_ids.join(', ')}</div>
      )}
    </div>
  );
}

function VerificationPanel({
  verification,
  edited = false,
}: {
  verification: { passed: boolean; flags: Flag[] };
  /** Edited since assembly: the checks describe the text as it was then. */
  edited?: boolean;
}) {
  const flags = verification?.flags || [];
  const stale = edited ? (
    <div className="text-xs mt-1" style={{ color: 'var(--ink-ink2)' }}>
      You’ve edited the draft since these checks ran, so they may no longer apply. The article’s own
      checks run again before you approve it.
    </div>
  ) : null;
  if (verification?.passed || flags.length === 0) {
    return (
      <div className="ink-card text-sm" style={{ padding: '10px 14px' }}>
        <div className="flex items-center gap-2" style={{ color: 'var(--ink-green-ink)' }}>
          <CheckCircle2 className="w-4 h-4" /> Checks passed: every claim links to a fact and the
          draft is within limits.
        </div>
        {stale}
      </div>
    );
  }
  const order = { high: 0, medium: 1, low: 2 } as const;
  const sorted = [...flags].sort((a, b) => order[a.severity] - order[b.severity]);
  return (
    <div className="rounded border border-cmp-line-2 bg-core-dark p-3">
      <div className="flex items-center gap-2 text-sm text-amber-300 mb-2">
        <AlertTriangle className="w-4 h-4" /> {flags.length}{' '}
        {flags.length === 1 ? 'thing to check' : 'things to check'} before you save
      </div>
      {stale}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {sorted.map((f, i) => (
          <div
            key={i}
            className={`rounded border px-3 py-2 text-xs ${SEVERITY_STYLES[f.severity]}`}
          >
            <div className="font-medium">
              {flagWords(f)[0]}
              {f.block_number ? ` · Block ${f.block_number}` : ''}
            </div>
            {f.problematic_text && <div className="mt-1 opacity-90">“{f.problematic_text}”</div>}
            <div className="mt-1 opacity-75">
              {f.type === 'outdated_future' ? f.explanation : flagWords(f)[1]}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
